import { Box, BoxProps, Center, SpinnerProps, Text, useToken } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { useMemo } from "react";

export interface RanaLoadingProps extends SpinnerProps {
  isCentered?: boolean;
  progress?: number;
}

const spinAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(-360deg);
  }
`;

const bubbleAnimation = keyframes`
  0% {
    top: 100%;
    transform: translateY(0);
  }
  100% {
    top: 0;
    transform: translateY(-100%);
  }
`;

const BUBBLE_COUNT = 10;

export function RanaLoading({
  boxSize: nonTokenBoxSize = "48px",
  progress,
  isCentered,
  ...rest
}: RanaLoadingProps) {
  const boxSize = useToken("sizes", (nonTokenBoxSize || "button") as string | number);
  const inProgress = progress !== undefined;

  const ProgressElement = ({ color }: { color: string }) => (
    <Center
      top={0}
      left={0}
      alignItems="center"
      position="absolute"
      h="full"
      w="full"
      color={color}
      fontSize={`calc(${boxSize} / 4)`}
      opacity={inProgress ? 1 : 0}
    >
      <Text>{Math.floor(progress ?? 0)}%</Text>
    </Center>
  );

  const clipPathPercentage = 100 - (progress || 0);

  const progressBubbleLoaderMemoPropsArray: BoxProps[] = useMemo(
    () =>
      [...Array(BUBBLE_COUNT).keys()].map(() => ({
        animation: `${bubbleAnimation} ${Math.random() * 2 + 1}s infinite`,
        left: `${Math.random() * 100}%`,
      })),
    []
  );
  return (
    <Center display={isCentered ? "flex" : "block"} {...rest}>
      <Box
        position="relative"
        boxSize={boxSize}
        rounded="full"
        overflow="hidden"
        transition="all 300ms"
      >
        <ProgressElement color="brand.analytic" />

        {/* Rotating loader bubbles */}
        {[...Array(BUBBLE_COUNT).keys()].map((_, i) => {
          const bubbleSize = `calc(${boxSize} / 20 * (${i} / 2))`;
          const angle = (300 / BUBBLE_COUNT) * i;

          return (
            <Box
              boxSize="full"
              key={"loading-bubbles-" + i}
              transform={`rotate(-${angle}deg)`}
              position="absolute"
            >
              <Box boxSize="full" animation={`${spinAnimation} 5s linear infinite`}>
                <Box
                  boxSize="full"
                  animation={`${spinAnimation} 2.5s ease-in-out infinite`}
                  style={{ animationDelay: `-${i * 50}ms` }}
                >
                  <Box
                    position="absolute"
                    left="1/2"
                    bottom={0}
                    boxSize={inProgress ? 0 : bubbleSize}
                    transform="translate(-50%, 0)"
                    bg="brand.analytic"
                    rounded="full"
                    transition="all 300ms"
                  />
                </Box>
              </Box>
            </Box>
          );
        })}

        {/* Progress animation */}
        <Box
          clipPath={`polygon(0 ${clipPathPercentage}%, 100% ${clipPathPercentage}%, 100% 100%, 0% 100%)`}
          bg="brand.analytic"
          transition="all 300ms"
          position="absolute"
          boxSize="full"
        >
          <ProgressElement color="brand.bg" />

          {progressBubbleLoaderMemoPropsArray.map((props, i) => {
            const size = `calc(5px + ${i}px)`;
            return (
              <Box
                {...props}
                key={"progress-bubbles-" + i}
                opacity={inProgress ? 1 : 0}
                transition="opacity 300ms"
                position="absolute"
                borderWidth="base"
                borderColor="brand.border"
                rounded="full"
                boxSize={size}
              />
            );
          })}
        </Box>
      </Box>
    </Center>
  );
}
